import { useState } from "react"

import DOMPurify from "dompurify"
import { useTranslation } from "react-i18next"

import type { UseCaseFormData } from "../../types/use-case.types"

import { Pencil, Sparkles } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import { cn } from "@/lib/utils"

import { AIResultDialog } from "./ai-result-dialog"
import { EditorModal } from "./editor-modal"
import { useAiDraftAssist } from "../../hooks/use-ai-draft-assist"

type FactSheetDescriptionsProps = {
  issue: string
  solutionDesc: string
  dataNeeded: string
  onChange: (field: string, value: string) => void
  getFormData?: () => UseCaseFormData
}

/** Strip HTML tags to get plain text for empty-check */
function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(DOMPurify.sanitize(html), "text/html")
  return doc.body.textContent.trim()
}

/** Read-only HTML preview block, clickable to open editor */
function ContentPreview({
  html,
  placeholder,
  className,
  onClick,
}: {
  html: string
  placeholder: string
  className?: string
  onClick: () => void
}) {
  const isEmpty = !stripHtml(html)
  const sanitized = DOMPurify.sanitize(html)

  return (
    <button
      type="button"
      className={cn(
        "group/preview bg-background/60 hover:bg-background/80 dark:bg-foreground/5 dark:hover:bg-foreground/10 relative w-full cursor-pointer rounded-md border px-3 py-2 text-left transition-colors",
        className
      )}
      onClick={onClick}
    >
      {isEmpty ? (
        <p className="text-muted-foreground text-xs italic">{placeholder}</p>
      ) : (
        <div
          className="prose dark:prose-invert prose-sm line-clamp-4 max-w-none text-xs leading-relaxed"
          dangerouslySetInnerHTML={{ __html: sanitized }}
        />
      )}
      <span className="absolute top-2 right-2 opacity-0 transition-opacity group-hover/preview:opacity-100">
        <Pencil className="text-muted-foreground size-3.5" />
      </span>
    </button>
  )
}

export function FactSheetDescriptions({
  issue,
  solutionDesc,
  dataNeeded,
  onChange,
  getFormData,
}: FactSheetDescriptionsProps) {
  const { t } = useTranslation()
  const draftAssist = useAiDraftAssist()
  const [draftDialogOpen, setDraftDialogOpen] = useState(false)
  const [activeField, setActiveField] = useState<string | null>(null)

  // Editor modal state
  const [editorOpen, setEditorOpen] = useState(false)
  const [editorField, setEditorField] = useState<string | null>(null)
  const [editorContent, setEditorContent] = useState("")
  const [editorTitle, setEditorTitle] = useState("")
  const [editorPlaceholder, setEditorPlaceholder] = useState("")

  const openEditor = ({
    field,
    content,
    title,
    placeholder,
  }: {
    field: string
    content: string
    title: string
    placeholder: string
  }) => {
    setEditorField(field)
    setEditorContent(content)
    setEditorTitle(title)
    setEditorPlaceholder(placeholder)
    setEditorOpen(true)
  }

  const handleEditorSave = (html: string) => {
    if (editorField) {
      onChange(editorField, html)
    }
  }

  const handleDraftAssist = (field: string) => {
    if (!getFormData) return
    setActiveField(field)
    setDraftDialogOpen(true)
    draftAssist.mutate({ useCaseData: getFormData(), targetField: field })
  }

  const handleAcceptDraft = (text: string) => {
    if (activeField) {
      onChange(activeField, text)
    }
  }

  const handleRegenerate = () => {
    if (!getFormData || !activeField) return
    draftAssist.mutate({ useCaseData: getFormData(), targetField: activeField })
  }

  const fieldLabels: Record<string, string> = {
    issue: "Problem Description",
    solutionDesc: "Solution Description",
    dataNeeded: "Data Sources",
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="border-destructive bg-destructive/10 rounded-lg border-l-4 p-3">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-destructive text-sm font-bold">
            {t("useCases.factSheet.descriptions.problem")}
          </h3>
          <div className="flex items-center gap-1">
            {getFormData && (
              <Button
                className="h-6 w-6 p-0"
                disabled={draftAssist.isPending && activeField === "issue"}
                size="sm"
                title="AI Draft Assist"
                variant="ghost"
                onClick={() => handleDraftAssist("issue")}
              >
                {draftAssist.isPending && activeField === "issue" ? (
                  <Spinner className="size-3.5" />
                ) : (
                  <Sparkles className="size-3.5" />
                )}
              </Button>
            )}
          </div>
        </div>
        <ContentPreview
          className="border-border"
          html={issue}
          placeholder={t("useCases.factSheet.descriptions.problemPlaceholder")}
          onClick={() =>
            openEditor({
              field: "issue",
              content: issue,
              title: t("useCases.factSheet.descriptions.problem"),
              placeholder: t("useCases.factSheet.descriptions.problemPlaceholder"),
            })
          }
        />
      </div>

      <div className="border-success bg-success/10 rounded-lg border-l-4 p-3">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-success text-sm font-bold">
            {t("useCases.factSheet.descriptions.solution")}
          </h3>
          <div className="flex items-center gap-1">
            {getFormData && (
              <Button
                className="h-6 w-6 p-0"
                disabled={draftAssist.isPending && activeField === "solutionDesc"}
                size="sm"
                title="AI Draft Assist"
                variant="ghost"
                onClick={() => handleDraftAssist("solutionDesc")}
              >
                {draftAssist.isPending && activeField === "solutionDesc" ? (
                  <Spinner className="size-3.5" />
                ) : (
                  <Sparkles className="size-3.5" />
                )}
              </Button>
            )}
          </div>
        </div>
        <ContentPreview
          className="border-border"
          html={solutionDesc}
          placeholder={t("useCases.factSheet.descriptions.solutionPlaceholder")}
          onClick={() =>
            openEditor({
              field: "solutionDesc",
              content: solutionDesc,
              title: t("useCases.factSheet.descriptions.solution"),
              placeholder: t("useCases.factSheet.descriptions.solutionPlaceholder"),
            })
          }
        />
      </div>

      <div className="bg-muted/40 border-border rounded-lg border-l-4 p-3">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-muted-foreground text-sm font-bold">
            {t("useCases.factSheet.descriptions.dataSources")}
          </h3>
          <div className="flex items-center gap-1">
            {getFormData && (
              <Button
                className="h-6 w-6 p-0"
                disabled={draftAssist.isPending && activeField === "dataNeeded"}
                size="sm"
                title="AI Draft Assist"
                variant="ghost"
                onClick={() => handleDraftAssist("dataNeeded")}
              >
                {draftAssist.isPending && activeField === "dataNeeded" ? (
                  <Spinner className="size-3.5" />
                ) : (
                  <Sparkles className="size-3.5" />
                )}
              </Button>
            )}
          </div>
        </div>
        <ContentPreview
          className="border-border"
          html={dataNeeded}
          placeholder={t("useCases.factSheet.descriptions.dataSourcesPlaceholder")}
          onClick={() =>
            openEditor({
              field: "dataNeeded",
              content: dataNeeded,
              title: t("useCases.factSheet.descriptions.dataSources"),
              placeholder: t("useCases.factSheet.descriptions.dataSourcesPlaceholder"),
            })
          }
        />
      </div>

      {/* Editor modal */}
      <EditorModal
        content={editorContent}
        open={editorOpen}
        placeholder={editorPlaceholder}
        title={editorTitle}
        onOpenChange={setEditorOpen}
        onSave={handleEditorSave}
      />

      {/* AI Draft dialog */}
      <AIResultDialog
        isLoading={draftAssist.isPending}
        open={draftDialogOpen}
        result={draftAssist.data?.text ?? null}
        title={`AI Draft: ${fieldLabels[activeField ?? ""] ?? "Content"}`}
        onAccept={handleAcceptDraft}
        onOpenChange={setDraftDialogOpen}
        onRegenerate={handleRegenerate}
      />
    </div>
  )
}
